import { MAX_EFFECTIVE_USER_FILE_IDS } from "../fileProvenance";
import type { SkillRunContext } from "./skillTurnBootstrap";

/**
 * The ordinary research scope the turn was submitted with, before any Skill
 * policy is applied. Both hosts already resolve this from the composer.
 */
export interface SubmittedResearchScope {
  readonly dataSources?: readonly string[];
  readonly userFileIds?: readonly string[];
}

export interface SkillResearchScope {
  /**
   * Exact resolved ordinary sources. `undefined` represents the resolver's
   * unrestricted (`null`) state and requires hosts to omit their source field.
   */
  readonly dataSources?: readonly string[];
  /**
   * Resolved Your Files scope, or `undefined` when neither the resolver nor a
   * Skill target contributes files and the submitted scope stays in force.
   */
  readonly userFileIds?: readonly string[];
}

/**
 * What the shared policy decided about one turn's research scope.
 *
 * The ceiling is never absorbed silently. A Skill whose targets do not fit
 * either stops the run before it starts or runs with every target withheld, so
 * no caller can present a partially attached Skill as a fully applied one.
 */
export type SkillResearchScopeOutcome =
  | {
      readonly status: "applied";
      readonly scope: SkillResearchScope;
      /** Skill target files that joined the run's Your Files scope. */
      readonly attachedTargetCount: number;
    }
  | {
      readonly status: "targets-omitted";
      readonly scope: SkillResearchScope;
      /** Targets withheld as one group; a partial attachment is never emitted. */
      readonly omittedTargetCount: number;
      readonly limit: number;
      readonly requestedFileCount: number;
    }
  | {
      readonly status: "blocked";
      readonly reason: "effective_file_limit";
      readonly limit: number;
      readonly requestedFileCount: number;
      readonly excessFileCount: number;
      readonly message: string;
    };

function normalizeFileIds(fileIds: readonly string[]): string[] {
  return Array.from(
    new Set(
      fileIds
        .filter((fileId): fileId is string => typeof fileId === "string")
        .map((fileId) => fileId.trim())
        .filter(Boolean)
    )
  );
}

function files(count: number): string {
  return `${count} file${count === 1 ? "" : "s"}`;
}

function overLimitMessage(
  targetCount: number,
  baseCount: number,
  requestedFileCount: number
): string {
  const excess = requestedFileCount - MAX_EFFECTIVE_USER_FILE_IDS;
  return [
    `This Skill attaches ${files(targetCount)} to the ${files(baseCount)} already selected,`,
    `which is ${requestedFileCount} in total — more than the ${MAX_EFFECTIVE_USER_FILE_IDS}`,
    "allowed per request.",
    baseCount > 0
      ? `Remove at least ${files(excess)} from your selection and try again.`
      : "Remove target files from the Skill and try again.",
  ].join(" ");
}

/**
 * Normalizes one resolved Skill run into the effective research scope for the
 * turn. Shared by both hosts so PowerPoint and Excel cannot drift on what a
 * Skill is allowed to change.
 *
 * Skill files are additive: a Skill's target files join the submitted Your
 * Files selection rather than replacing the ordinary sources. The resolver's
 * tri-state is preserved exactly — `null` means unrestricted and omits the
 * source field, while `[]` is an authoritative empty scope.
 *
 * When the combined selection exceeds the per-request ceiling, the policy
 * follows the resolver's own explicit/inferred split: a Skill the user chose
 * stops the turn so they can reduce their selection, while an inferred Skill
 * fails open by withholding its targets. Truncating to fit is deliberately not
 * an option — it produces a run that claims target files it cannot read.
 *
 * `USER_FILES` is deliberately not added here. The wire boundary appends it
 * exactly once when the request carries file ids, matching the non-Skill path.
 */
export function resolveSkillResearchScope(
  context: SkillRunContext,
  submitted: SubmittedResearchScope = {}
): SkillResearchScopeOutcome {
  const { effectiveDataSources } = context;
  const { userFileIds: resolvedFileIds, skillTargetFiles } = context.effectiveExplorerContext;

  // The resolver only returns targets it could authenticate and confirm ready,
  // so every entry here is eligible to participate in research.
  const targetFileIds = normalizeFileIds(skillTargetFiles.map((file) => file.id));
  const baseFileIds = resolvedFileIds === null ? submitted.userFileIds : resolvedFileIds;
  // The resolver is authoritative and may introduce a newly supported source
  // before this client catalog is updated. Preserve values verbatim;
  // downstream entitlement/tool filtering remains authoritative.
  const sourceScope = effectiveDataSources === null ? {} : { dataSources: effectiveDataSources };
  const scopeWithoutTargets: SkillResearchScope = {
    ...sourceScope,
    ...(resolvedFileIds === null ? {} : { userFileIds: normalizeFileIds(resolvedFileIds) }),
  };

  if (targetFileIds.length === 0) {
    return { status: "applied", scope: scopeWithoutTargets, attachedTargetCount: 0 };
  }

  const unionedFileIds = normalizeFileIds([...(baseFileIds ?? []), ...targetFileIds]);
  if (unionedFileIds.length > MAX_EFFECTIVE_USER_FILE_IDS) {
    // Only a Skill's targets can push the scope past the ceiling here: a
    // submitted selection that is already over it is rejected at the composer
    // and again at the query boundary, and neither answer changes because a
    // Skill is active.
    if (context.selectionSource === "explicit") {
      return {
        status: "blocked",
        reason: "effective_file_limit",
        limit: MAX_EFFECTIVE_USER_FILE_IDS,
        requestedFileCount: unionedFileIds.length,
        excessFileCount: unionedFileIds.length - MAX_EFFECTIVE_USER_FILE_IDS,
        message: overLimitMessage(
          targetFileIds.length,
          normalizeFileIds(baseFileIds ?? []).length,
          unionedFileIds.length
        ),
      };
    }
    return {
      status: "targets-omitted",
      scope: scopeWithoutTargets,
      omittedTargetCount: targetFileIds.length,
      limit: MAX_EFFECTIVE_USER_FILE_IDS,
      requestedFileCount: unionedFileIds.length,
    };
  }

  return {
    status: "applied",
    scope: { ...sourceScope, userFileIds: unionedFileIds },
    attachedTargetCount: targetFileIds.length,
  };
}
